import React, { useState } from "react";
import { Typography, Box } from "@mui/material";
import PasswordIcon from "@mui/icons-material/Password";
import SyncIcon from "@mui/icons-material/Sync";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PrimaryButton from "../../../widgets/Buttons/PrimaryButton";
import SecondaryButton from "../../../widgets/Buttons/SecondaryButton";
import ActionCard from "../../../widgets/ActionCard/ActionCard";
import CustomModal from "../../../widgets/Modal/CustomModal";
import ErrorSnackbar from "../../../widgets/Snackbar/ErrorSnackbar";
import SuccessSnackbar from "../../../widgets/Snackbar/SuccessSnackbar";
import { useGetOtp } from "../../../hooks/useGetOtp";
import { useResetOtp } from "../../../hooks/useResetOtp";
import { useUnblockUser } from "../../../hooks/useUnblockUser";
import { Height } from "@mui/icons-material";

const OtpManagementCard = ({ phone, features, studentData, verticalInfo }) => {
  const {
    mutate: getOtp,
    isPending: isGetOtpPending,
    isLoading: isGetOtpLoading,
  } = useGetOtp();
  const isGettingOtp = isGetOtpPending || isGetOtpLoading;

  const {
    mutate: resetOtp,
    isPending: isResetOtpPending,
    isLoading: isResetOtpLoading,
  } = useResetOtp();
  const isResettingOtp = isResetOtpPending || isResetOtpLoading;

  const {
    mutate: unblockUser,
    isPending: isUnblockUserPending,
    isLoading: isUnblockUserLoading,
  } = useUnblockUser();
  const isUnblockingUser = isUnblockUserPending || isUnblockUserLoading;

  const [otpModal, setOtpModal] = useState({ open: false, code: "" });
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    actionText: "",
  });
  const [errorSnackbar, setErrorSnackbar] = useState({
    open: false,
    message: "",
  });
  const [successSnackbar, setSuccessSnackbar] = useState({
    open: false,
    message: "",
  });

  const handleGetOtp = () => {
    getOtp(
      {
        phone,
        isd_code: "+91",
        tenant_id: verticalInfo?.tenantId || "1",
        product_id: verticalInfo?.productId || "300",
      },
      {
        onSuccess: (res) => {
          const code =
            res?.otpCode ||
            res?.data?.otpCode ||
            res?.otp_code ||
            res?.data?.otp_code;
          if (code) {
            setOtpModal({ open: true, code });
            setSuccessSnackbar({
              open: true,
              message: "OTP fetched successfully.",
            });
          } else {
            setErrorSnackbar({ open: true, message: "OTP not found." });
          }
        },
        onError: (err) => {
          setErrorSnackbar({
            open: true,
            message: err.message || "Failed to fetch OTP.",
          });
        },
      },
    );
  };

  const handleConfirmYes = () => {
    const action = confirmModal.actionText;
    setConfirmModal({ open: false, actionText: "" });

    if (action === "Reset OTP") {
      resetOtp(
        { phone, isd_code: "+91" },
        {
          onSuccess: () => {
            setSuccessSnackbar({
              open: true,
              message: "OTP reset successfully.",
            });
          },
          onError: (err) => {
            setErrorSnackbar({
              open: true,
              message: err.message || "Failed to reset OTP.",
            });
          },
        },
      );
    } else if (action === "Unblock User") {
      unblockUser(
        { admission_id: studentData?.admission_number },
        {
          onSuccess: () => {
            setSuccessSnackbar({
              open: true,
              message: "User unblocked successfully.",
            });
          },
          onError: (err) => {
            setErrorSnackbar({
              open: true,
              message: err.message || "Failed to unblock user.",
            });
          },
        },
      );
    }
  };

  return (
    <>
      <ActionCard title="OTP & Password Management">
        {features.getOtp && (
          <PrimaryButton
            startIcon={<PasswordIcon />}
            onClick={handleGetOtp}
            isLoading={isGettingOtp}
            sx={{ height: "35px" }}
          >
            {isGettingOtp ? "Fetching..." : "Get OTP"}
          </PrimaryButton>
        )}
        {features.resetOtp && (
          <PrimaryButton
            startIcon={<SyncIcon />}
            onClick={() =>
              setConfirmModal({ open: true, actionText: "Reset OTP" })
            }
            sx={{ height: "35px" }}
            isLoading={isResettingOtp}
          >
            {isResettingOtp ? "Resetting..." : "Reset OTP"}
          </PrimaryButton>
        )}
        {features.unblock && (
          <PrimaryButton
            startIcon={<LockOpenIcon />}
            onClick={() =>
              setConfirmModal({ open: true, actionText: "Unblock User" })
            }
            sx={{ height: "35px" }}
            isLoading={isUnblockingUser}
          >
            {isUnblockingUser ? "Unblocking..." : "Unblock User"}
          </PrimaryButton>
        )}
      </ActionCard>

      <CustomModal
        open={otpModal.open}
        onClose={() => setOtpModal({ open: false, code: "" })}
        title="Generated OTP"
        maxWidth="xs"
        actions={
          <SecondaryButton
            onClick={() => setOtpModal({ open: false, code: "" })}
          >
            Close
          </SecondaryButton>
        }
      >
        <Box
          sx={{
            textAlign: "center",
            py: 3,
            bgcolor: "#f8fafc",
            borderRadius: 2,
            border: "1px dashed #cbd5e1",
          }}
        >
          <Typography
            variant="h3"
            sx={{ letterSpacing: "8px", fontWeight: "bold", color: "#1d4ed8" }}
          >
            {otpModal.code}
          </Typography>
        </Box>
      </CustomModal>

      <CustomModal
        open={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, actionText: "" })}
        title="Confirm Action"
        maxWidth="xs"
        actions={
          <>
            <SecondaryButton
              onClick={() => setConfirmModal({ open: false, actionText: "" })}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={handleConfirmYes}>Confirm</PrimaryButton>
          </>
        }
      >
        <Typography sx={{ color: "#334155", fontSize: "1rem" }}>
          Are you sure you want to proceed with{" "}
          <strong>{confirmModal.actionText}</strong>?
        </Typography>
      </CustomModal>

      <ErrorSnackbar
        open={errorSnackbar.open}
        message={errorSnackbar.message}
        onClose={() => setErrorSnackbar({ open: false, message: "" })}
      />
      <SuccessSnackbar
        open={successSnackbar.open}
        message={successSnackbar.message}
        onClose={() => setSuccessSnackbar({ open: false, message: "" })}
      />
    </>
  );
};

export default OtpManagementCard;
